import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { isAxiosError } from "axios";
import { useAnonymousActions } from "@frontend/shared/features/actions/application/actions/use-anonymous-actions";
import { i18n } from "@frontend/i18n";
import { resolveRedirect } from "@frontend/shared/infrastructure/routing/resolve-user-route-redirect";

interface MagicLinkControllerReturn {
  readonly state: {
    readonly errorMessage: string | null;
    readonly isSuccess: boolean;
  };
}

export function useMagicLinkController(): MagicLinkControllerReturn {
  const { magicLinkIdPublic } = useParams<{ magicLinkIdPublic: string }>();

  const { mutateAsync } = useAnonymousActions().useProcessMagicLink();

  // Track success/error locally: in React StrictMode the mutation observer is
  // destroyed in its mount→cleanup→mount cycle, so a per-call onSuccess redirect
  // is orphaned and the page hangs on "Processing". mutateAsync settles
  // regardless (mirrors useOtpAuthController / useOAuthCallbackController).
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Prevent double-execution in React StrictMode
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (
      hasStartedRef.current ||
      magicLinkIdPublic === undefined ||
      magicLinkIdPublic === ""
    ) {
      return;
    }

    hasStartedRef.current = true;

    void mutateAsync(magicLinkIdPublic).then(
      (data) => {
        setIsSuccess(true);
        window.location.href = resolveRedirect(data.redirect);
      },

      (caughtError: unknown) => {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError)),
        );
      },
    );
  }, [magicLinkIdPublic, mutateAsync]);

  const errorMessage = deriveErrorMessage(magicLinkIdPublic, error);

  return { state: { errorMessage, isSuccess } };
}

function deriveErrorMessage(
  magicLinkIdPublic: string | undefined,
  error: Error | null,
): string | null {
  if (magicLinkIdPublic === undefined || magicLinkIdPublic === "") {
    return i18n.t("magicLink.error.noId");
  }

  if (error !== null) {
    return isAxiosError(error) && error.response?.status === 400
      ? i18n.t("magicLink.error.invalid")
      : i18n.t("magicLink.error.generic");
  }

  return null;
}
